import Icon from '../ui/Icon';
import { Skeleton } from '../ui/Skeleton';
import { ValueEditor } from './QueryBuilder';
import { FIELD_ICONS } from './fieldIcons';
import type { QueryField, QueryRule, QueryValue } from './types';

export type QueryFilterSummary = {
  id: string;
  field: string;
  fieldLabel: string;
  operatorLabel: string;
  valueLabel: string;
  valueLoading?: boolean;
  fieldDef?: QueryField;
  rule?: QueryRule;
};

// Only these field types get click-to-edit on the chip value today - their
// controls (CustomSelect, DatePicker) already manage their own open/close
// popover, so they can sit inline as plain text and still work in one click.
// Everything else (text, boolean, textarea, ranges, etc.) stays display-only.
const EDITABLE_FIELD_TYPES = new Set(['select', 'date', 'datetime']);

export default function QueryFilterSummaryChips({
  summaries,
  onRemove,
  onValueChange,
}: {
  summaries: QueryFilterSummary[];
  onRemove: (ruleId: string) => void;
  onValueChange?: (ruleId: string, value: QueryValue) => void;
}) {
  if (!summaries.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {summaries.map(summary => {
        const FieldIcon = FIELD_ICONS[summary.field];
        const canEditValue = Boolean(
          summary.fieldDef &&
          summary.rule &&
          onValueChange &&
          (EDITABLE_FIELD_TYPES.has(summary.fieldDef.type) ||
            (summary.fieldDef.type === 'number' && !!summary.fieldDef.options?.length))
        );
        const showValueSkeleton = Boolean(
          summary.valueLoading ||
          (summary.fieldDef?.type === 'select' &&
            summary.fieldDef.searchLoading &&
            summary.rule &&
            !summary.valueLabel)
        );
        return (
          <div
            key={summary.id}
            className="flex h-[26px] items-center overflow-hidden rounded-md border border-border bg-background text-xs"
          >
            <span className="flex h-full items-center gap-1 border-r border-border px-2 text-muted-foreground font-medium">
              {FieldIcon && <FieldIcon size={12} className="shrink-0" />}
              {summary.fieldLabel}
            </span>
            <span className="flex h-full items-center border-r border-border px-2 font-medium text-muted-foreground">
              {summary.operatorLabel}
            </span>
            <span
              className={`flex h-full items-center font-medium text-foreground ${canEditValue ? '' : 'px-2'}`}
            >
              {showValueSkeleton ? (
                <div className="flex h-full min-w-[120px] items-center px-2">
                  <Skeleton className="h-4 w-full rounded-sm" />
                </div>
              ) : canEditValue ? (
                <ValueEditor
                  field={summary.fieldDef as QueryField}
                  rule={summary.rule as QueryRule}
                  compact
                  inline
                  onChange={value => onValueChange?.(summary.id, value)}
                />
              ) : summary.fieldDef?.tagItem && summary.rule ? (
                <span className="flex items-center gap-1">
                  {(Array.isArray(summary.rule.value)
                    ? summary.rule.value
                    : [summary.rule.value]
                  ).map((value, index) => (
                    <span key={index}>
                      {summary.fieldDef?.tagItem?.({ label: String(value), value: String(value) })}
                    </span>
                  ))}
                </span>
              ) : (
                summary.valueLabel
              )}
            </span>
            <button
              type="button"
              className="flex h-full items-center border-l border-border px-1.5 text-muted-foreground hover:text-foreground"
              aria-label={`Remove ${summary.fieldLabel} filter`}
              onClick={() => onRemove(summary.id)}
            >
              <Icon name="cross-fill" size={4} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
